import { saveAs } from 'file-saver';
import { apiService } from '../../../services/apiService';
import {
  API_PATHS,
  getTransactionDccDetailPath,
  getMerchantWebhookUrlPath,
  getMarkTransactionRefundPath,
  getMarkTransactionChargebackPath,
  GET_TRANSACTION_DCC_DETAILS_BATCH,
} from '../../../utils/apiPaths';
import { createCompleteGridFetcher } from '../../../utils/fetchCompleteGridList';
import { GRID_LIST_FETCH_SIZE } from '../../../components/shared/agGridCells';
import type { OrderListRequest, OrderModeFilter, TransactionDccDetail, TransactionFilterLookups } from '../models/types';
import { EMPTY_TRANSACTION_COLUMN_FILTERS } from '../constants/constants';
import {
  buildOrdersPayload,
  mapApiAnalytics,
  mapApiOrder,
} from '../utils/utils';

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const utfMatch = /filename\*=(?:UTF-8''|)([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      return utfMatch[1].trim().replace(/^["']|["']$/g, '');
    }
  }
  const plainMatch = /filename=(["']?)([^";]+)\1/i.exec(header);
  return plainMatch?.[2]?.trim() || fallback;
}

async function messageFromBlob(blob: Blob, fallback: string): Promise<string> {
  try {
    const text = await blob.text();
    if (!text) return fallback;
    const parsed = JSON.parse(text) as { message?: string; error?: string };
    return parsed.message || parsed.error || fallback;
  } catch {
    return fallback;
  }
}

// For a referral admin, GET_PLATFORM_USERS is self-scoped server-side (PlatformUserServiceImpl
// force-narrows it to their own row regardless of the request body) — always fetches fresh,
// authoritative merchant names instead of trusting whatever was on the login response (which could
// be stale if the session predates a merchant being (re)assigned, or simply predates this field
// existing at all).
export async function fetchOwnReferralMerchants(): Promise<{ id: number; name: string }[]> {
  const res: any = await apiService.post(API_PATHS.GET_PLATFORM_USERS, {});
  if (!res?.success) {
    return [];
  }
  const rows: any[] = res.data?.content ?? res.data?.admins ?? [];
  const own = rows[0];
  const ids: number[] = Array.isArray(own?.assignedMerchantIds) ? own.assignedMerchantIds : [];
  const names: string[] = Array.isArray(own?.assignedMerchantNames) ? own.assignedMerchantNames : [];
  return ids.map((id, index) => ({ id: Number(id), name: names[index] ?? '' })).filter((m) => m.name);
}

function extractListContent(res: any): Record<string, unknown>[] {
  const content = res?.data?.content ?? res?.data;
  return Array.isArray(content) ? content : [];
}

async function fetchCompleteNamedOptions(
  path: string,
  getName: (item: Record<string, unknown>) => string,
): Promise<string[]> {
  const request = async (size: number, exportData: boolean) => {
    const res: any = await apiService.post(path, { page: 0, size, export: exportData });
    if (!res?.success) {
      throw new Error(res?.message || `Failed to load ${path}`);
    }
    const rows = extractListContent(res);
    return {
      rows,
      totalElements: Number(res.data?.totalElements ?? rows.length),
    };
  };

  const first = await request(GRID_LIST_FETCH_SIZE, false);
  const full =
    first.rows.length >= first.totalElements || first.rows.length < GRID_LIST_FETCH_SIZE
      ? first
      : await request(first.totalElements, first.totalElements > GRID_LIST_FETCH_SIZE);

  const names = new Set<string>();
  for (const item of full.rows) {
    const name = getName(item).trim();
    if (name && name !== '—') names.add(name);
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

export async function fetchTransactionFilterLookups(): Promise<TransactionFilterLookups> {
  const results = await Promise.allSettled([
    fetchCompleteNamedOptions(API_PATHS.GET_MERCHANTS, (item) =>
      String(item.name ?? item.merchantName ?? ''),
    ),
    fetchCompleteNamedOptions(API_PATHS.GET_ACQUIRERS, (item) =>
      String(item.name ?? item.acquirerName ?? ''),
    ),
    fetchCompleteNamedOptions(API_PATHS.GET_ALL_ACQUIRER_TERMINALS, (item) =>
      String(item.name ?? item.terminalName ?? item.acquirerTerminalName ?? ''),
    ),
    fetchCompleteNamedOptions(API_PATHS.GET_ALL_MERCHANT_TERMINALS, (item) =>
      String(item.name ?? item.terminalName ?? item.merchantTerminalName ?? ''),
    ),
  ]);

  const names = (result: PromiseSettledResult<string[]>) =>
    result.status === 'fulfilled' ? result.value : [];

  if (results.some((result) => result.status === 'rejected')) {
    console.error(
      'Some transaction filter lookups failed:',
      results.filter((result) => result.status === 'rejected'),
    );
  }

  return {
    merchantName: names(results[0]),
    acquirerName: names(results[1]),
    acquirerTerminalName: names(results[2]),
    merchantTerminalName: names(results[3]),
  };
}

export async function fetchOrdersList(payload: OrderListRequest) {
  const res: any = await apiService.post(API_PATHS.GET_ORDERS, payload);
  if (!res?.success) {
    throw new Error('Failed to fetch orders');
  }

  const payloadData = res.data ?? {};
  const orderContent = payloadData.content ?? res.data;
  const rows = Array.isArray(orderContent)
    ? orderContent.map((t: Record<string, unknown>) => mapApiOrder(t))
    : [];

  return {
    rows,
    analytics: mapApiAnalytics(payloadData.analytics),
    totalElements: Number(payloadData.totalElements ?? rows.length),
  };
}

export async function exportOrders(payload: OrderListRequest): Promise<void> {
  let response;
  try {
    response = await apiService.postBlob(API_PATHS.EXPORT_ORDERS, payload);
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: unknown }; message?: string };
    const data = axiosErr.response?.data;
    if (data instanceof Blob) {
      throw new Error(await messageFromBlob(data, axiosErr.message || 'Failed to export orders'));
    }
    const jsonMessage =
      data && typeof data === 'object' && 'message' in data
        ? String((data as { message?: unknown }).message || '')
        : '';
    throw new Error(jsonMessage || axiosErr.message || 'Failed to export orders');
  }

  const blob = response.data;
  const contentType = String(response.headers['content-type'] ?? blob?.type ?? '');
  if (contentType.includes('application/json')) {
    throw new Error(await messageFromBlob(blob, 'Failed to export orders'));
  }
  if (!blob || blob.size === 0) {
    throw new Error('No orders available to export for the current filter.');
  }

  saveAs(
    blob,
    filenameFromContentDisposition(
      response.headers['content-disposition'],
      `orders-${Date.now()}.xlsx`,
    ),
  );
}

export async function fetchOrderAnalytics(page = 0, size = 10000) {
  const res: any = await apiService.post(API_PATHS.GET_ORDER_ANALYTICS, { page, size });

  // Supports both { success, data: analytics } and a flat analytics body.
  const payload =
    res?.data && typeof res.data === 'object' && !Array.isArray(res.data)
      ? res.data
      : res;

  if (res?.success === false) {
    throw new Error(res?.message || 'Failed to fetch order analytics');
  }

  const raw =
    payload &&
    typeof payload === 'object' &&
    ('totalVolume' in payload ||
      'countryTransactions' in payload ||
      'volumeTrend' in payload ||
      'hourlyTrend' in payload)
      ? payload
      : payload?.analytics;

  if (!raw || typeof raw !== 'object') {
    throw new Error(res?.message || 'Failed to fetch order analytics');
  }

  return mapApiAnalytics(raw);
}

export async function fetchAllOrdersForStats(
  fullCount?: number,
  modeFilter?: OrderModeFilter,
) {
  const res: any = await apiService.post(
    API_PATHS.GET_ORDERS,
    buildOrdersPayload({
      filter: 'all',
      search: '',
      columnFilters: EMPTY_TRANSACTION_COLUMN_FILTERS,
      modeFilter,
      page: 0,
      size: fullCount && fullCount > 0 ? fullCount : GRID_LIST_FETCH_SIZE,
      export: Boolean(fullCount && fullCount > GRID_LIST_FETCH_SIZE),
    }),
  );

  if (!res?.success) {
    throw new Error('Failed to fetch order stats');
  }

  const payloadData = res.data ?? {};
  const orderContent = payloadData.content ?? res.data;
  const rows = Array.isArray(orderContent)
    ? orderContent.map((t: Record<string, unknown>) => mapApiOrder(t))
    : [];

  return {
    rows,
    totalElements: Number(payloadData.totalElements ?? rows.length),
  };
}

export const fetchOrdersGrid = createCompleteGridFetcher((
  options: Parameters<typeof buildOrdersPayload>[0],
) => fetchOrdersList(buildOrdersPayload(options)));

export async function fetchOrderTransactions(orderId: string) {
  const res: any = await apiService.post(API_PATHS.GET_TRANSACTIONS, {
    orderId,
    page: 0,
    size: 5,
  });

  if (!res?.success) {
    throw new Error('Failed to fetch transactions');
  }

  const content = res.data?.content ?? res.data;
  return Array.isArray(content) ? content : [];
}

export async function fetchTransactionDccDetail(transactionId: string) {
  const res: any = await apiService.get(getTransactionDccDetailPath(transactionId));
  return res?.success ? res.data : null;
}

export async function resendWebhook(merchantId: string, transactionId: string) {
  const res: any = await apiService.get(getMerchantWebhookUrlPath(merchantId), {
    params: { transactionId },
  });

  if (!res?.success) {
    throw new Error(res?.message || 'Failed to resend webhook');
  }

  return res;
}

// Manual super-admin override — no acquirer API call, purely a DB marker on the backend. Inserts a
// NEW transaction row rather than mutating the existing one, so the row that was current when the
// admin clicked stays an untouched historical record.
export async function markTransactionRefund(transactionId: string, reason?: string) {
  const res: any = await apiService.patch(
    getMarkTransactionRefundPath(transactionId),
    reason ? { reason } : {},
  );

  if (!res?.success) {
    throw new Error(res?.message || 'Failed to mark transaction as refunded');
  }

  return res;
}

export async function markTransactionChargeback(transactionId: string, reason?: string) {
  const res: any = await apiService.patch(
    getMarkTransactionChargebackPath(transactionId),
    reason ? { reason } : {},
  );

  if (!res?.success) {
    throw new Error(res?.message || 'Failed to mark transaction as chargeback');
  }

  return res;
}

// One HTTP call (and one DB round trip) for however many transaction ids are passed, instead of
// firing fetchTransactionDccDetail once per id — the grid used to do the latter for every visible
// row, which fanned out into enough parallel requests to be visibly slow against a DB connection
// with high per-round-trip latency.
export async function fetchTransactionDccDetailsBatch(
  transactionIds: string[],
): Promise<Record<string, TransactionDccDetail>> {
  if (transactionIds.length === 0) return {};
  const res: any = await apiService.post(GET_TRANSACTION_DCC_DETAILS_BATCH, { transactionIds });
  const list = res?.success && Array.isArray(res.data) ? res.data : [];
  return Object.fromEntries(
    list
      .filter((item: TransactionDccDetail | null) => item?.transactionId)
      .map((item: TransactionDccDetail) => [item.transactionId, item]),
  );
}
